import { computed, ref } from 'vue'
import { fetchMe, login as loginRequest } from '@/api/auth'
import {
  clearAuthTokens,
  getApiErrorMessage,
  getStoredRefreshToken,
  getStoredToken,
  performTokenRefresh,
  setAuthTokens,
} from '@/api/client'
import { wireAuthHandlers } from '@/composables/authBridge'
import type { LoginRequest, RefreshTokenResponse, Usuario } from '@/types/api'

const accessToken = ref<string | null>(getStoredToken())
const usuario = ref<Usuario | null>(null)
const initialized = ref(false)
let initializePromise: Promise<void> | null = null

async function restoreSessionWithRefresh(): Promise<boolean> {
  const refreshToken = getStoredRefreshToken()
  if (!refreshToken) {
    return false
  }

  try {
    const tokens = await performTokenRefresh()
    applyTokens(tokens)
    return true
  } catch {
    return false
  }
}

async function loadSession(): Promise<void> {
  if (!accessToken.value) {
    usuario.value = null
    return
  }

  try {
    usuario.value = await fetchMe()
  } catch {
    const restored = await restoreSessionWithRefresh()
    if (!restored) {
      clearAuthTokens()
      accessToken.value = null
      usuario.value = null
      return
    }

    try {
      usuario.value = await fetchMe()
    } catch {
      clearAuthTokens()
      accessToken.value = null
      usuario.value = null
    }
  }
}

function applyTokens(tokens: RefreshTokenResponse): void {
  setAuthTokens(tokens.access_token, tokens.refresh_token, tokens.expires_in)
  accessToken.value = tokens.access_token
}

async function logout(): Promise<void> {
  clearAuthTokens()
  accessToken.value = null
  usuario.value = null
}

export function useAuth() {
  const isAuthenticated = computed(() => accessToken.value !== null && usuario.value !== null)

  async function initialize(): Promise<void> {
    if (initialized.value) {
      return
    }

    if (!initializePromise) {
      initializePromise = loadSession().finally(() => {
        initialized.value = true
      })
    }

    await initializePromise
  }

  async function login(credentials: LoginRequest): Promise<void> {
    const response = await loginRequest(credentials)
    setAuthTokens(response.access_token, response.refresh_token, response.expires_in)
    accessToken.value = response.access_token
    usuario.value = response.usuario
    initialized.value = true
  }

  function getErrorMessage(error: unknown, fallback = 'Erro inesperado'): string {
    return getApiErrorMessage(error, fallback)
  }

  return {
    accessToken,
    usuario,
    initialized,
    isAuthenticated,
    initialize,
    login,
    logout,
    applyTokens,
    getErrorMessage,
  }
}

wireAuthHandlers({
  applyTokens,
  logout,
})
